import { DownOutlined, EditOutlined } from '@ant-design/icons'
import { Dropdown, Menu, MenuProps, PageHeader, Typography } from 'antd'
import { useAtom } from 'jotai'
import { isNumber, isString } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { ResourceRolePermissionDialog } from '@/components/resource-permissions-configuration/resource-role-permissions-configuration'
import { ResourceWhitelistPermissionDialog } from '@/components/resource-permissions-configuration/resource-whitelist-permissions-configuration'
import { routerConfig } from '@/router'
import { ResourceType } from '@/schemas'

import { userInfoAtom } from '../user'
import { AvatarDropdown } from './avatar-dropdown.component'
import { CreateDropdown } from './create-dropdown.component'

interface HeaderProps {
  title?: React.ReactNode
  actions?: React.ReactNode[]
  resourceType?: ResourceType
  leftExtra?: React.ReactNode
}

export const Header: React.FC<HeaderProps> = ({ title, resourceType, leftExtra, actions }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const location = useLocation()

  const [visible, setVisible] = React.useState(false)
  const [roleDialogVisible, setRoleDialogVisible] = React.useState(false)
  const [whitelistDialogVisible, setWhitelistDialogVisible] = React.useState(false)

  const onClickMenu: MenuProps['onClick'] = ({ key }) => {
    if (key === 'whitelist') {
      setWhitelistDialogVisible(true)
    }
    if (key === 'rolePermission') {
      setRoleDialogVisible(true)
    }
    setVisible(false)
  }

  const menu = (
    <Menu onClick={onClickMenu}>
      <Menu.Item key='whitelist' icon={<EditOutlined />}>
        <span> {t('resource.whitelist')}</span>
      </Menu.Item>
      <Menu.Item key='rolePermission' icon={<EditOutlined />}>
        <span> {t('resource.rolePermission')}</span>
      </Menu.Item>
    </Menu>
  )

  const currentRouter = routerConfig.find((c) => location.pathname.startsWith(c.path))

  const createHeaderTitle = () => {
    if (title && !isString(title)) {
      return title
    } else if (currentRouter) {
      return (
        <div className='flex space-x-4 items-center'>
          {currentRouter.path !== '/personal' ? (
            viewer.isSuperAdmin ? (
              <Dropdown
                trigger={['click']}
                overlay={menu}
                visible={visible}
                onVisibleChange={(visible) => setVisible(visible)}
              >
                <Typography.Text ellipsis className='cursor-pointer text-xl'>
                  <span className='mr-12px'>{currentRouter.icon}</span>
                  {t(currentRouter.title)}
                  {title}
                  <DownOutlined className='ml-4px' />
                </Typography.Text>
              </Dropdown>
            ) : (
              <Typography.Text ellipsis className='text-xl'>
                <span className='mr-12px'>{currentRouter.icon}</span>
                {t(currentRouter.title)}
                {title}
              </Typography.Text>
            )
          ) : (
            <>
              <span className='mr-12px'>{currentRouter.icon}</span>
              {t(currentRouter.title)}
              {title}
            </>
          )}
          {leftExtra}
          {isNumber(resourceType) && (
            <>
              <ResourceWhitelistPermissionDialog
                visible={whitelistDialogVisible}
                onVisibleChange={setWhitelistDialogVisible}
                resourceType={resourceType}
              />
              <ResourceRolePermissionDialog
                visible={roleDialogVisible}
                onVisibleChange={setRoleDialogVisible}
                resourceType={resourceType}
              />
            </>
          )}
        </div>
      )
    }
  }

  const extra = [...(actions || []), <CreateDropdown key='create' />, <AvatarDropdown key='avatar' />]

  return <PageHeader ghost={false} backIcon={false} title={createHeaderTitle()} extra={extra} />
}
